import React, { useState, useEffect } from 'react';
import { tenderAPI } from '../utils/api';
import { Plus, Loader2, Play, Trash2, Edit2, X, Check, AlertTriangle, RefreshCw, Radar } from 'lucide-react';

function TenderSources() {
  var s1 = useState([]); var sources = s1[0]; var setSources = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(null); var editSource = s3[0]; var setEditSource = s3[1];
  var s4 = useState(false); var showModal = s4[0]; var setShowModal = s4[1];
  var s5 = useState([]); var logs = s5[0]; var setLogs = s5[1];
  var s6 = useState({}); var runStatus = s6[0]; var setRunStatus = s6[1];
  var s7 = useState(false); var showAIDiscover = s7[0]; var setShowAIDiscover = s7[1];

  useEffect(function() { fetchData(); }, []);

  var fetchData = async function() {
    try {
      var r1 = await tenderAPI.sources();
      setSources(r1.data);
      var r2 = await tenderAPI.scrapeLog({ limit: 20 });
      setLogs(r2.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  var handleRun = async function(sourceId) {
    var newSt = {}; var keys = Object.keys(runStatus); for (var k = 0; k < keys.length; k++) newSt[keys[k]] = runStatus[keys[k]];
    newSt[sourceId] = 'running'; setRunStatus(newSt);
    try {
      await tenderAPI.runSource(sourceId);
      newSt[sourceId] = 'done';
    } catch (e) { newSt[sourceId] = 'error'; }
    setRunStatus({...newSt});
    await fetchData();
  };

  var handleTest = async function(sourceId) {
    var newSt = {}; var keys = Object.keys(runStatus); for (var k = 0; k < keys.length; k++) newSt[keys[k]] = runStatus[keys[k]];
    newSt[sourceId] = 'testing'; setRunStatus(newSt);
    try {
      var res = await tenderAPI.testSource(sourceId);
      var d = res.data;
      newSt[sourceId] = d.success ? 'test_ok_' + d.tenders_found : 'test_fail';
    } catch (e) { newSt[sourceId] = 'test_fail'; }
    setRunStatus({...newSt});
  };

  var handleDelete = async function(sourceId) {
    if (!window.confirm('Delete this source?')) return;
    try { await tenderAPI.deleteSource(sourceId); await fetchData(); } catch (e) { console.error(e); }
  };

  var handleToggle = async function(source) {
    try { await tenderAPI.updateSource(source._id, { enabled: !source.enabled }); await fetchData(); } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  var sourceCards = [];
  for (var i = 0; i < sources.length; i++) {
    var src = sources[i];
    var st = runStatus[src._id] || '';
    var isRunning = st === 'running' || st === 'testing';
    var errored = src.consecutive_errors >= 3;
    sourceCards.push(
      <div key={src._id} className={'bg-white rounded-lg border p-5 ' + (errored ? 'border-red-200' : 'border-slate-200')} data-testid={'source-card-' + src._id}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-slate-900">{src.name}</h3>
              <span className={'text-xs px-2 py-0.5 rounded ' + (src.enabled ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400')}>{src.enabled ? 'Active' : 'Disabled'}</span>
              {errored && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-600 flex items-center gap-1"><AlertTriangle size={10} /> Auto-disabled</span>}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{src.type} | {src.method} | {src.schedule}</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={function(id) { return function() { handleTest(id); }; }(src._id)} disabled={isRunning} className="p-1.5 text-slate-400 hover:text-primary hover:bg-slate-50 rounded" title="Test"><RefreshCw size={14} className={st === 'testing' ? 'animate-spin' : ''} /></button>
            <button onClick={function(id) { return function() { handleRun(id); }; }(src._id)} disabled={isRunning} className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-slate-50 rounded" title="Run"><Play size={14} /></button>
            <button onClick={function(s) { return function() { setEditSource(s); setShowModal(true); }; }(src)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded"><Edit2 size={14} /></button>
            <button onClick={function(id) { return function() { handleDelete(id); }; }(src._id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-slate-50 rounded"><Trash2 size={14} /></button>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span>Last: {src.last_scraped_at ? new Date(src.last_scraped_at).toLocaleString() : 'Never'}</span>
          <span>Found: {src.last_result_count || 0}</span>
          <span>Errors: {src.consecutive_errors || 0}</span>
          <button onClick={function(s) { return function() { handleToggle(s); }; }(src)} className="text-primary hover:underline">{src.enabled ? 'Disable' : 'Enable'}</button>
        </div>
        {st && st.startsWith('test_ok') && <p className="text-xs text-green-600 mt-2">Test OK: {st.split('_')[2]} tenders found</p>}
        {st === 'test_fail' && <p className="text-xs text-red-600 mt-2">Test failed</p>}
        {st === 'done' && <p className="text-xs text-green-600 mt-2">Scrape complete</p>}
        {st === 'error' && <p className="text-xs text-red-600 mt-2">Scrape failed</p>}
      </div>
    );
  }

  var logRows = [];
  for (var li = 0; li < logs.length; li++) {
    var log = logs[li];
    var stCls = log.status === 'success' ? 'text-green-600' : log.status === 'failed' ? 'text-red-600' : 'text-amber-600';
    logRows.push(
      <tr key={log._id} className="border-b border-slate-100">
        <td className="py-2 px-3 text-sm">{log.source_name}</td>
        <td className={'py-2 px-3 text-sm ' + stCls}>{log.status}</td>
        <td className="py-2 px-3 text-sm">{log.tenders_found || 0}</td>
        <td className="py-2 px-3 text-sm">{log.tenders_new || 0}</td>
        <td className="py-2 px-3 text-sm text-slate-400">{log.created_date ? new Date(log.created_date).toLocaleString() : '-'}</td>
        <td className="py-2 px-3 text-xs text-red-500 max-w-[200px] truncate">{log.error_message || '-'}</td>
      </tr>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tender-sources-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">Tender Sources</h1><p className="text-sm text-slate-500">{sources.length} organisation websites configured</p></div>
        <div className="flex gap-2">
          <button onClick={function() { setShowAIDiscover(true); }} className="px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 flex items-center gap-2 text-sm" data-testid="ai-discover-sources-btn"><Radar size={16} /> AI Discover More</button>
          <button onClick={function() { setEditSource(null); setShowModal(true); }} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 text-sm" data-testid="add-source-btn"><Plus size={16} /> Add Source</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{sourceCards}</div>

      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Scrape Log</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-slate-200"><th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Source</th><th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Status</th><th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Found</th><th className="py-2 px-3 text-left text-xs font-medium text-slate-500">New</th><th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Date</th><th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Error</th></tr></thead>
            <tbody>{logRows.length > 0 ? logRows : <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No scrape history yet</td></tr>}</tbody>
          </table>
        </div>
      </div>

      {showModal && <SourceModal source={editSource} onClose={function() { setShowModal(false); setEditSource(null); }} onSuccess={function() { setShowModal(false); setEditSource(null); fetchData(); }} />}
      {showAIDiscover && <AIDiscoverSourcesModal onClose={function() { setShowAIDiscover(false); }} onSuccess={function() { setShowAIDiscover(false); fetchData(); }} />}
    </div>
  );
}

function SourceModal({ source, onClose, onSuccess }) {
  var s1 = useState(source ? source.name : ''); var name = s1[0]; var setName = s1[1];
  var s2 = useState(source ? source.url : ''); var url = s2[0]; var setUrl = s2[1];
  var s3 = useState(source ? (source.search_url || '') : ''); var searchUrl = s3[0]; var setSearchUrl = s3[1];
  var s4 = useState(source ? source.type : 'government_portal'); var type = s4[0]; var setType = s4[1];
  var s5 = useState(source ? source.method : 'web_scrape'); var method = s5[0]; var setMethod = s5[1];
  var s6 = useState(source ? source.schedule : 'daily'); var schedule = s6[0]; var setSchedule = s6[1];
  var s7 = useState(source ? (source.search_terms || []).join(', ') : ''); var terms = s7[0]; var setTerms = s7[1];
  var s8 = useState(false); var saving = s8[0]; var setSaving = s8[1];

  var handleSave = async function() {
    setSaving(true);
    try {
      var data = { name: name, url: url, search_url: searchUrl || null, type: type, method: method, schedule: schedule, search_terms: terms ? terms.split(',').map(function(t) { return t.trim(); }).filter(Boolean) : [] };
      if (source) { await tenderAPI.updateSource(source._id, data); }
      else { await tenderAPI.createSource(data); }
      onSuccess();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{source ? 'Edit Source' : 'Add Source'}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Name *</label><input value={name} onChange={function(e) { setName(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="source-name-input" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">URL *</label><input value={url} onChange={function(e) { setUrl(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Search URL</label><input value={searchUrl} onChange={function(e) { setSearchUrl(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Type</label><select value={type} onChange={function(e) { setType(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="government_portal">Government</option><option value="aggregator">Aggregator</option><option value="glc_portal">GLC</option><option value="search_engine">Search Engine</option></select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Method</label><select value={method} onChange={function(e) { setMethod(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="web_scrape">Web Scrape</option><option value="google_search">Google Search</option><option value="rss">RSS</option><option value="api">API</option></select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Schedule</label><select value={schedule} onChange={function(e) { setSchedule(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="hourly">Hourly</option><option value="daily">Daily</option><option value="weekly">Weekly</option></select></div>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Search Terms</label><input value={terms} onChange={function(e) { setTerms(e.target.value); }} placeholder="comma-separated keywords" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !name || !url} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50" data-testid="save-source-btn">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default TenderSources;

function AIDiscoverSourcesModal({ onClose, onSuccess }) {
  var s1 = useState(false); var loading = s1[0]; var setLoading = s1[1];
  var s2 = useState([]); var suggestions = s2[0]; var setSuggestions = s2[1];
  var s3 = useState({}); var selected = s3[0]; var setSelected = s3[1];
  var s4 = useState(false); var saving = s4[0]; var setSaving = s4[1];
  var s5 = useState(null); var error = s5[0]; var setError = s5[1];

  var handleDiscover = async function() {
    setLoading(true);
    setError(null);
    try {
      var res = await tenderAPI.aiDiscoverSources();
      var sugs = res.data.suggestions || [];
      setSuggestions(sugs);
      var sel = {};
      for (var i = 0; i < sugs.length; i++) sel[i] = true;
      setSelected(sel);
    } catch (e) { setError('AI discovery failed. Try again.'); }
    finally { setLoading(false); }
  };

  var toggleSelect = function(idx) {
    var ns = {}; var k = Object.keys(selected);
    for (var i = 0; i < k.length; i++) ns[k[i]] = true;
    if (ns[idx]) delete ns[idx]; else ns[idx] = true;
    setSelected(ns);
  };

  var handleAdd = async function() {
    var toAdd = [];
    for (var i = 0; i < suggestions.length; i++) { if (selected[i]) toAdd.push(suggestions[i]); }
    if (toAdd.length === 0) return;
    setSaving(true);
    try {
      await tenderAPI.batchAddSources(toAdd);
      onSuccess();
    } catch (e) { setError('Failed to add sources'); }
    finally { setSaving(false); }
  };

  var selCount = Object.keys(selected).length;
  var sugItems = [];
  for (var i = 0; i < suggestions.length; i++) {
    var s = suggestions[i];
    var isSel = !!selected[i];
    sugItems.push(
      <div key={i} onClick={function(idx) { return function() { toggleSelect(idx); }; }(i)} className={'p-3 rounded-lg border cursor-pointer transition-colors ' + (isSel ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300')}>
        <div className="flex items-start gap-3">
          <div className={'w-4 h-4 mt-1 rounded border flex items-center justify-center flex-shrink-0 ' + (isSel ? 'bg-primary border-primary' : 'border-slate-300')}>{isSel && <Check size={12} className="text-white" />}</div>
          <div><p className="text-sm font-medium text-slate-900">{s.name}</p><p className="text-xs text-primary">{s.search_url}</p><p className="text-xs text-slate-400">{s.type} | {s.notes}</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-discover-sources-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={function(e) { e.stopPropagation(); }}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div><h2 className="text-lg font-semibold">AI Source Discovery</h2><p className="text-sm text-slate-500">Claude AI finds more organisation procurement pages</p></div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {suggestions.length === 0 && !loading && (
            <div className="text-center py-8">
              <p className="text-sm text-slate-600 mb-4">Claude AI will search for Malaysian government agencies, GLCs, and PLCs that publish procurement/tender notices on their websites.</p>
              <button onClick={handleDiscover} className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 mx-auto" data-testid="discover-sources-btn"><Radar size={16} /> Discover Organisations</button>
            </div>
          )}
          {loading && <div className="text-center py-8"><Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-slate-500">Discovering organisations...</p></div>}
          {suggestions.length > 0 && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Found {suggestions.length} organisations. {selCount} selected.</p>
              {sugItems}
              <button onClick={handleAdd} disabled={saving || selCount === 0} className="w-full py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="batch-add-sources-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add {selCount} Sources</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
